import {
  Fragment,
  createContext,
  useContext,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';

import {
  parseUiDocument,
  type ActionSpec,
  type BindingExpr,
  type InternalNavigationTarget,
  type JsonValue,
  type UiDocumentV1,
  type UiNode,
} from '../../mdr-ui-contracts/src/index.js';

export interface RuntimeUiError {
  readonly code: 'UNKNOWN_COMPONENT' | 'BINDING_ERROR' | 'ACTION_ERROR';
  readonly screenId: string;
  readonly nodeId: string;
  readonly detail: string;
}

export interface UiComponentProps {
  readonly node: UiNode;
  readonly props: Readonly<Record<string, JsonValue>>;
  readonly children: ReactNode;
  readonly copy: (key: string) => string;
  readonly runAction: (action: ActionSpec, localInput?: JsonValue) => Promise<unknown>;
}

export type UiComponent = ComponentType<UiComponentProps>;
export type UiComponentRegistry = Readonly<Record<string, UiComponent>>;
export type UiComponentLoader = () => Promise<unknown>;
export type UiComponentLoaderRegistry = Readonly<Record<string, UiComponentLoader>>;
export type ActionExecutor = (operationId: string, input: JsonValue) => Promise<unknown>;
export type InternalNavigator = (target: InternalNavigationTarget) => void;

export function createUiComponentLoaderCatalog(
  registries: readonly UiComponentLoaderRegistry[],
): UiComponentLoaderRegistry {
  const catalog: Record<string, UiComponentLoader> = {};
  for (const registry of registries) {
    for (const [componentId, loader] of Object.entries(registry)) {
      if (catalog[componentId]) {
        throw new Error(`Duplicate UI component loader ${componentId}`);
      }
      catalog[componentId] = loader;
    }
  }
  return Object.freeze(catalog);
}

function isUiComponent(value: unknown): value is UiComponent {
  return typeof value === 'function' || (typeof value === 'object' && value !== null);
}

export async function loadUiComponentRegistry(
  componentIds: readonly string[],
  loaders: UiComponentLoaderRegistry,
): Promise<UiComponentRegistry> {
  const uniqueIds = [...new Set(componentIds)].sort();
  const entries = await Promise.all(
    uniqueIds.map(async (componentId) => {
      const loader = loaders[componentId];
      if (!loader) throw new Error(`Missing UI component loader ${componentId}`);
      const component = await loader();
      if (!isUiComponent(component)) {
        throw new Error(`UI component loader ${componentId} returned an invalid component`);
      }
      return [componentId, component] as const;
    }),
  );
  return Object.freeze(Object.fromEntries(entries));
}

interface RendererContextValue {
  readonly document: UiDocumentV1;
  readonly registry: UiComponentRegistry;
  readonly domainRegistry: UiComponentRegistry;
  readonly data: Readonly<Record<string, JsonValue>>;
  readonly copy: Readonly<Record<string, string>>;
  readonly execute: ActionExecutor;
  readonly navigate: InternalNavigator;
  readonly report: (error: RuntimeUiError) => void;
}

const RendererContext = createContext<RendererContextValue | null>(null);

function isJsonRecord(value: unknown): value is { readonly [key: string]: JsonValue } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readPath(data: Readonly<Record<string, JsonValue>>, path: string): JsonValue {
  const parts = path.split('.');
  let current: JsonValue = data;
  for (const part of parts) {
    if (!isJsonRecord(current)) {
      throw new Error(`Cannot read ${path}; ${part} is not an object field`);
    }
    const next: JsonValue | undefined = current[part];
    if (next === undefined) throw new Error(`Binding field ${path} is missing`);
    current = next;
  }
  return current;
}

function isExpression(value: JsonValue): value is BindingExpr {
  return isJsonRecord(value) && typeof value['kind'] === 'string';
}

function truthy(value: JsonValue): boolean {
  return value !== false && value !== null && value !== '' && value !== 0;
}

export function evaluateBinding(
  expression: BindingExpr,
  data: Readonly<Record<string, JsonValue>>,
  formatters: Readonly<Record<string, (value: JsonValue) => JsonValue>> = {},
  aggregates: Readonly<Record<string, (value: JsonValue) => JsonValue>> = {},
): JsonValue {
  switch (expression.kind) {
    case 'field':
      return readPath(data, expression.path);
    case 'literal':
      return expression.value;
    case 'coalesce':
      for (const value of expression.values) {
        const evaluated = evaluateBinding(value, data, formatters, aggregates);
        if (evaluated !== null && evaluated !== '') return evaluated;
      }
      return null;
    case 'eq': {
      const [left, right] = expression.operands.map((operand) =>
        evaluateBinding(operand, data, formatters, aggregates),
      );
      return JSON.stringify(left) === JSON.stringify(right);
    }
    case 'in': {
      const [needle, haystack] = expression.operands.map((operand) =>
        evaluateBinding(operand, data, formatters, aggregates),
      );
      return Array.isArray(haystack)
        ? haystack.some((candidate) => JSON.stringify(candidate) === JSON.stringify(needle))
        : false;
    }
    case 'and':
      return expression.operands.every((operand) =>
        truthy(evaluateBinding(operand, data, formatters, aggregates)),
      );
    case 'or':
      return expression.operands.some((operand) =>
        truthy(evaluateBinding(operand, data, formatters, aggregates)),
      );
    case 'not':
      return !truthy(evaluateBinding(expression.operand, data, formatters, aggregates));
    case 'exists':
      try {
        return evaluateBinding(expression.operand, data, formatters, aggregates) !== null;
      } catch {
        return false;
      }
    case 'format': {
      const formatter = formatters[expression.id];
      if (!formatter) throw new Error(`Unknown formatter ${expression.id}`);
      return formatter(evaluateBinding(expression.input, data, formatters, aggregates));
    }
    case 'aggregate': {
      const aggregate = aggregates[expression.id];
      if (!aggregate) throw new Error(`Unknown aggregate ${expression.id}`);
      return aggregate(evaluateBinding(expression.input, data, formatters, aggregates));
    }
  }
}

function resolveProps(
  props: Readonly<Record<string, JsonValue>>,
  data: Readonly<Record<string, JsonValue>>,
): Readonly<Record<string, JsonValue>> {
  return Object.fromEntries(
    Object.entries(props).map(([key, value]) => [
      key,
      isExpression(value) ? evaluateBinding(value, data) : value,
    ]),
  );
}

function useRenderer(): RendererContextValue {
  const context = useContext(RendererContext);
  if (!context) throw new Error('MDR renderer context is missing');
  return context;
}

function ErrorNode({ message }: { readonly message: string }) {
  return (
    <div className="mdr-alert mdr-alert--error" role="alert">
      <strong>畫面元件暫時無法載入</strong>
      <span>{message}</span>
    </div>
  );
}

function NodeRenderer({ nodeId }: { readonly nodeId: string }) {
  const context = useRenderer();
  const node = context.document.nodes[nodeId];
  const [actionError, setActionError] = useState<string | null>(null);
  if (!node) return <ErrorNode message={`缺少節點 ${nodeId}`} />;

  let visible = true;
  try {
    visible = node.visibleWhen ? truthy(evaluateBinding(node.visibleWhen, context.data)) : true;
  } catch (error) {
    context.report({
      code: 'BINDING_ERROR',
      screenId: context.document.screenId,
      nodeId,
      detail: error instanceof Error ? error.message : String(error),
    });
    return <ErrorNode message="顯示條件無法解析，已停止渲染這個區塊。" />;
  }
  if (!visible) return null;

  const componentKey =
    node.type === 'DomainComponent' ? (node.componentId ?? 'missing-domain-component') : node.type;
  const Component =
    node.type === 'DomainComponent'
      ? context.domainRegistry[componentKey]
      : context.registry[componentKey];
  if (!Component) {
    context.report({
      code: 'UNKNOWN_COMPONENT',
      screenId: context.document.screenId,
      nodeId,
      detail: componentKey,
    });
    return <ErrorNode message={`未註冊元件：${componentKey}`} />;
  }

  const runAction = async (action: ActionSpec, localInput?: JsonValue): Promise<unknown> => {
    try {
      const actionData =
        localInput === undefined ? context.data : { ...context.data, component: localInput };
      const input = evaluateBinding(action.input, actionData);
      const result = await context.execute(action.operationId, input);
      setActionError(null);
      if (action.onSuccess) context.navigate(action.onSuccess);
      return result;
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      setActionError(detail);
      context.report({
        code: 'ACTION_ERROR',
        screenId: context.document.screenId,
        nodeId,
        detail,
      });
      return undefined;
    }
  };

  let props: Readonly<Record<string, JsonValue>>;
  try {
    props = resolveProps(node.props, context.data);
  } catch (error) {
    context.report({
      code: 'BINDING_ERROR',
      screenId: context.document.screenId,
      nodeId,
      detail: error instanceof Error ? error.message : String(error),
    });
    return <ErrorNode message="資料欄位無法解析，請重新整理後再試。" />;
  }

  return (
    <Fragment>
      <Component
        node={node}
        props={props}
        copy={(key) => context.copy[key] ?? key}
        runAction={runAction}
      >
        {node.children.map((childId) => (
          <NodeRenderer key={childId} nodeId={childId} />
        ))}
      </Component>
      {actionError ? <ErrorNode message={actionError} /> : null}
    </Fragment>
  );
}

function copyProp(
  props: Readonly<Record<string, JsonValue>>,
  name: string,
  copy: (key: string) => string,
): string {
  const value = props[name];
  return typeof value === 'string' ? copy(value) : '';
}

const Container = ({ node, children }: UiComponentProps) => (
  <div className={`mdr-${node.type.toLowerCase()}`}>{children}</div>
);

const Page = ({ props, children, copy }: UiComponentProps) => (
  <main className="mdr-page">
    {typeof props['eyebrowCopyKey'] === 'string' ? (
      <p className="mdr-eyebrow">{copy(props['eyebrowCopyKey'])}</p>
    ) : null}
    {typeof props['titleCopyKey'] === 'string' ? <h1>{copy(props['titleCopyKey'])}</h1> : null}
    {typeof props['descriptionCopyKey'] === 'string' ? (
      <p className="mdr-lead">{copy(props['descriptionCopyKey'])}</p>
    ) : null}
    {children}
  </main>
);

const Section = ({ props, children, copy }: UiComponentProps) => (
  <section className="mdr-section">
    {typeof props['titleCopyKey'] === 'string' ? <h2>{copy(props['titleCopyKey'])}</h2> : null}
    {typeof props['descriptionCopyKey'] === 'string' ? (
      <p>{copy(props['descriptionCopyKey'])}</p>
    ) : null}
    {children}
  </section>
);

const Text = ({ props, copy }: UiComponentProps) => {
  const value = props['value'];
  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  return <p>{props['copyKey'] ? copyProp(props, 'copyKey', copy) : text}</p>;
};

const Metric = ({ props, copy }: UiComponentProps) => {
  const value = props['value'];
  const rawValueCopyKeys = props['valueCopyKeys'];
  const mappedCopyKey =
    typeof value === 'string' &&
    isJsonRecord(rawValueCopyKeys) &&
    typeof rawValueCopyKeys[value] === 'string'
      ? rawValueCopyKeys[value]
      : undefined;
  return (
    <article className="mdr-metric">
      <span>{copyProp(props, 'labelCopyKey', copy)}</span>
      <strong>
        {typeof props['valueCopyKey'] === 'string'
          ? copy(props['valueCopyKey'])
          : mappedCopyKey
            ? copy(mappedCopyKey)
            : String(value ?? '—')}
      </strong>
    </article>
  );
};

const TrustBadge = ({ props, copy }: UiComponentProps) => (
  <div className="mdr-trust-badge">
    <span aria-hidden="true">✓</span>
    <span>{copyProp(props, 'labelCopyKey', copy)}</span>
  </div>
);

const CTA = ({ node, props, copy, runAction }: UiComponentProps) => (
  <button
    className={`mdr-cta mdr-cta--${String(props['emphasis'] ?? 'secondary')}`}
    type="button"
    onClick={() => {
      if (node.action) void runAction(node.action);
    }}
  >
    {copyProp(props, 'labelCopyKey', copy)}
  </button>
);

const Accordion = ({ props, children, copy }: UiComponentProps) => (
  <details className="mdr-accordion">
    <summary>{copyProp(props, 'labelCopyKey', copy)}</summary>
    {children}
  </details>
);

interface TableColumn {
  readonly field: string;
  readonly labelCopyKey: string;
  readonly valueCopyKeys?: Readonly<Record<string, string>>;
}

function tableColumns(value: JsonValue | undefined): readonly TableColumn[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((candidate) => {
    if (!isJsonRecord(candidate)) return [];
    const field = candidate['field'];
    const labelCopyKey = candidate['labelCopyKey'];
    const rawValueCopyKeys = candidate['valueCopyKeys'];
    const valueCopyKeys =
      isJsonRecord(rawValueCopyKeys) &&
      Object.values(rawValueCopyKeys).every((copyKey) => typeof copyKey === 'string')
        ? (rawValueCopyKeys as Readonly<Record<string, string>>)
        : undefined;
    return typeof field === 'string' && typeof labelCopyKey === 'string'
      ? [{ field, labelCopyKey, ...(valueCopyKeys ? { valueCopyKeys } : {}) }]
      : [];
  });
}

function cellValue(row: Readonly<Record<string, JsonValue>>, field: string): string {
  let value: JsonValue = row;
  for (const segment of field.split('.')) {
    if (!isJsonRecord(value)) return '—';
    value = value[segment] ?? null;
  }
  if (value === null || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const DataTable = ({ props, copy }: UiComponentProps) => {
  const columns = tableColumns(props['columns']);
  const rows = Array.isArray(props['rows']) ? props['rows'].filter(isJsonRecord) : [];
  if (rows.length === 0) {
    return <p className="mdr-empty">{copyProp(props, 'emptyCopyKey', copy)}</p>;
  }
  return (
    <div className="mdr-table-wrap">
      <table className="mdr-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.field} scope="col">
                {copy(column.labelCopyKey)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`${cellValue(row, columns[0]?.field ?? '')}-${rowIndex}`}>
              {columns.map((column) => (
                <td key={column.field}>
                  {column.valueCopyKeys?.[cellValue(row, column.field)]
                    ? copy(column.valueCopyKeys[cellValue(row, column.field)]!)
                    : cellValue(row, column.field)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const defaultComponentRegistry: UiComponentRegistry = Object.freeze({
  Page,
  Stack: Container,
  Grid: Container,
  Section,
  Text,
  Metric,
  TrustBadge,
  CTA,
  Accordion,
  Alert: Container,
  Badge: Container,
  Help: Container,
  List: Container,
  Table: DataTable,
  Detail: Container,
  Timeline: Container,
});

export interface MdrRendererProps {
  readonly document: unknown;
  readonly data?: Readonly<Record<string, JsonValue>>;
  readonly copy: Readonly<Record<string, string>>;
  readonly registry?: UiComponentRegistry;
  readonly domainRegistry?: UiComponentRegistry;
  readonly execute: ActionExecutor;
  readonly navigate?: InternalNavigator;
  readonly onError?: (error: RuntimeUiError) => void;
}

export function MdrRenderer({
  document: rawDocument,
  data = {},
  copy,
  registry = defaultComponentRegistry,
  domainRegistry = {},
  execute,
  navigate = () => undefined,
  onError = () => undefined,
}: MdrRendererProps) {
  const document = useMemo(() => parseUiDocument(rawDocument), [rawDocument]);
  const context = useMemo<RendererContextValue>(
    () => ({
      document,
      registry,
      domainRegistry,
      data,
      copy,
      execute,
      navigate,
      report: onError,
    }),
    [copy, data, document, domainRegistry, execute, navigate, onError, registry],
  );
  return (
    <RendererContext.Provider value={context}>
      <NodeRenderer nodeId={document.rootNodeId} />
    </RendererContext.Provider>
  );
}
